import { SearchIcon } from "@minket/brand/icons/search";
import { Kbd } from "@minket/ui/components/kbd";
import { focusRingSidebar } from "@minket/ui/lib/styles";
import { cn } from "@minket/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { type Section, sectionsFor } from "@/lib/navigation";
import { usePlace } from "@/lib/place";
import { useWorkspace } from "@/lib/workspace";
import { AccountControl, prefetchPalette, SwitcherControl, ThemeControl } from "./deferred";
import { Wordmark } from "./wordmark";

/**
 * A row on the paper rail: the word with its drawn icon at 20 px, on one line, 40 px apart (36
 * on a window under 640 px tall, 32 under 560), so the nine sections fit any window from 480 px
 * tall without the rail scrolling.
 */
export const railRow = cn(
  focusRingSidebar,
  "group relative flex h-10 w-full min-w-0 items-center gap-3 rounded-md px-2.5 text-left text-[15px] font-medium whitespace-nowrap text-sidebar-foreground transition-colors duration-150 ease-out-quiet hover:bg-sidebar-accent hover:text-sidebar-accent-foreground short:h-9 [@media(max-height:559px)]:h-8",
);

/**
 * The section in view is tied to the thread: its word in semibold, its icon in signature ink, a
 * 3 px signature-ink marker on its left edge, and a dotted plate-ink leader running from its word
 * to a knot on the thread, as a contents page leads an entry to its page number. Where you are
 * reads from across the room, in the rail's own line work rather than a filled pill.
 */
const current = cn(
  "before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-r-[2px] before:bg-sidebar-primary before:opacity-0",
  "data-[status=active]:font-semibold data-[status=active]:text-sidebar-foreground data-[status=active]:before:opacity-100 data-[status=active]:hover:bg-sidebar-accent/60",
);

/** The stage of a non-production build, so a dev console is never mistaken for production. */
const stage =
  import.meta.env.VITE_STAGE && import.meta.env.VITE_STAGE !== "production"
    ? import.meta.env.VITE_STAGE
    : null;

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);
export const paletteKey = isMac ? "⌘K" : "Ctrl K";

function SectionLink({
  section,
  active,
  second,
  onNavigate,
}: {
  section: Section;
  active: boolean;
  /** The first of the second run: a 16 px gap above it (8 on a short window). */
  second: boolean;
  onNavigate?: () => void;
}) {
  const Icon = section.icon;
  return (
    <li className={second ? "mt-4 short:mt-2" : undefined}>
      <Link
        to={section.tabs[0].to}
        activeOptions={{ exact: true }}
        onClick={onNavigate}
        data-status={active ? "active" : undefined}
        aria-current={active ? "true" : undefined}
        className={cn(railRow, current)}
      >
        <Icon
          size={20}
          className="shrink-0 text-muted-foreground group-data-[status=active]:text-sidebar-primary"
        />
        {section.label}
        <span
          aria-hidden
          className="-mr-4 ml-1 hidden h-0 min-w-4 flex-1 border-t-[1.5px] border-dotted border-seal-ink group-data-[status=active]:block"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 -right-4 z-10 size-2.5 -translate-y-1/2 rounded-full border-2 border-sidebar bg-seal-ink opacity-0 group-data-[status=active]:opacity-100"
        />
      </Link>
    </li>
  );
}

/** The second run of the rail starts here: what is set up once (domains, developers, settings). */
const SECOND_RUN = "domains";

/**
 * The rail (direction §7, nine sections for the owner): the wordmark, the scope, the sections in
 * two runs (the daily work, then what is set up once) parted by a gap rather than a heading, and
 * Search, the theme and the account at the foot. It never scrolls: below 640 px of height the
 * rows close to 36 px and the foot folds onto one line (`short:`). The same content fills the
 * phone sheet.
 */
export function SidebarContent({
  onNavigate,
  onOpenPalette,
}: {
  /** Called after a link is followed (the phone menu closes itself). */
  onNavigate?: () => void;
  onOpenPalette: () => void;
}) {
  const { section } = usePlace();
  const { user } = useWorkspace();
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="grid gap-3 px-3.5 pt-4 pb-3 short:gap-2 short:pt-3 short:pb-2">
        <div className="flex min-w-0 items-center gap-2 pr-10 lg:pr-0">
          <Link
            to="/"
            onClick={onNavigate}
            className={cn(focusRingSidebar, "flex items-center rounded-md px-1 py-1")}
          >
            <Wordmark height={22} />
            <span className="sr-only">Minket overview</span>
          </Link>
          {stage ? (
            <span className="rounded-sm border border-sidebar-border px-1.5 py-px font-mono text-[11px] whitespace-nowrap text-muted-foreground">
              {stage}
            </span>
          ) : null}
        </div>
        <SwitcherControl onNavigate={onNavigate} />
      </div>
      <nav aria-label="Console" className="min-h-0 flex-1 px-2.5 pt-1 pb-2">
        <ul className="grid gap-0.5">
          {sectionsFor(user.platform_admin).map((entry) => (
            <SectionLink
              key={entry.key}
              section={entry}
              active={section?.key === entry.key}
              second={entry.key === SECOND_RUN}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      </nav>
      <div className="grid gap-1 border-t border-seal-ink/30 p-2.5 short:flex short:items-center short:gap-1 short:py-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <button
            type="button"
            onClick={onOpenPalette}
            onPointerEnter={prefetchPalette}
            onFocus={prefetchPalette}
            className={cn(railRow, "min-h-9 flex-1 text-sm")}
          >
            <SearchIcon size={20} className="shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">Search</span>
            <Kbd
              aria-hidden
              className="border-sidebar-border short:hidden [@media(pointer:coarse)]:hidden"
            >
              {paletteKey}
            </Kbd>
          </button>
          <ThemeControl />
        </div>
        <AccountControl />
      </div>
    </div>
  );
}
